import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { DataTable } from "@/components/tables/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { useDriverRankings } from "@/features/rankings/useRankings";
import { formatDateTime, formatPercent } from "@/utils/format";
import type { DriverRanking } from "@/types/domain";

const PAGE_SIZE = 20;

export default function Rankings() {
  const [page, setPage] = useState(1);
  const rankings = useDriverRankings(page, PAGE_SIZE);

  const columns = useMemo<ColumnDef<DriverRanking, unknown>[]>(
    () => [
      { header: "#", accessorKey: "rank" },
      { header: "Driver", accessorFn: (r) => r.driverName ?? r.driverId },
      { header: "Rating", accessorFn: (r) => (r.ratingCount > 0 ? `★ ${r.rating.toFixed(1)}` : "—") },
      { header: "Trips (30d)", accessorKey: "trips30d" },
      { header: "Accept %", accessorFn: (r) => formatPercent(r.acceptanceRate) },
      { header: "Cancel %", accessorFn: (r) => formatPercent(r.cancelRate) },
      { header: "₹/km", accessorFn: (r) => `₹${r.perKmRate}` },
      { header: "Score", accessorFn: (r) => r.score.toFixed(1) },
    ],
    [],
  );

  return (
    <PageContainer
      title="Driver leaderboard"
      description={
        rankings.data?.computedAt
          ? `Ranked by the rating service · last computed ${formatDateTime(rankings.data.computedAt)}`
          : "Ranked by the rating service — not recomputed here."
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={rankings.data?.items ?? []}
          isLoading={rankings.isLoading}
          error={rankings.error}
          onRetry={() => rankings.refetch()}
          rowKey={(r) => r.driverId}
          emptyTitle="No ranking data yet"
        />
        {rankings.data && (
          <Pagination page={page} pageSize={PAGE_SIZE} total={rankings.data.total} onPageChange={setPage} />
        )}
      </div>
    </PageContainer>
  );
}
